// src/context/AuthContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import {
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
} from "firebase/auth";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { auth, db, googleProvider, facebookProvider } from "../firebase";

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

// ─── WHITELIST CHECK ──────────────────────────────────────────────────────────
// Reads /whitelist/{email} — this collection allows unauthenticated reads
// (see firestore.rules) because this check fires BEFORE a session exists.
async function isEmailWhitelisted(email) {
  if (!email) return false;
  const normalizedEmail = email.toLowerCase().trim();

  try {
    const ref  = doc(db, "whitelist", normalizedEmail);
    const snap = await getDoc(ref);
    return snap.exists() && snap.data()?.approved === true;
  } catch (err) {
    // If this throws it almost always means Firestore rules still block
    // unauthenticated reads on /whitelist.
    // Fix: deploy firestore.rules with `allow read: if true` on /whitelist/{email}
    console.error(
      "[Aimble Auth] Whitelist check failed.\n" +
      "Most likely cause: Firestore rules block unauthenticated reads on /whitelist.\n" +
      "Fix: deploy firestore.rules with `allow read: if true` on /whitelist/{email}.\n" +
      "Original error:", err
    );

    throw new Error(
      "ACCESS_DENIED: Unable to verify access. " +
      "Please contact info@aimbletech.com or try again later."
    );
  }
}

// ─── AUTH PROVIDER ────────────────────────────────────────────────────────────
export function AuthProvider({ children }) {
  const [currentUser,  setCurrentUser]  = useState(null);
  const [userProfile,  setUserProfile]  = useState(null);
  const [loading,      setLoading]      = useState(true);

  async function createUserDoc(user, extra = {}) {
    const ref  = doc(db, "users", user.uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
      await setDoc(ref, {
        uid:         user.uid,
        email:       user.email?.toLowerCase().trim(),
        displayName: user.displayName || extra.name || "",
        photoURL:    user.photoURL || "",
        role:        "client",
        createdAt:   serverTimestamp(),
        ...extra,
      });
    }
    const updated = await getDoc(ref);
    setUserProfile(updated.data());
  }

  async function signInWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    const email  = result.user.email?.toLowerCase().trim();

    const allowed = await isEmailWhitelisted(email);
    if (!allowed) {
      await signOut(auth);
      throw new Error(
        "ACCESS_DENIED: This Google account is not on the approved list. " +
        "Please contact info@aimbletech.com to request access."
      );
    }

    await createUserDoc(result.user);
    return result;
  }

  async function signInWithFacebook() {
    const result = await signInWithPopup(auth, facebookProvider);
    const email  = result.user.email?.toLowerCase().trim();

    const allowed = await isEmailWhitelisted(email);
    if (!allowed) {
      await signOut(auth);
      throw new Error(
        "ACCESS_DENIED: This Facebook account is not on the approved list. " +
        "Please contact info@aimbletech.com to request access."
      );
    }

    await createUserDoc(result.user);
    return result;
  }

  async function signInWithEmail(email, password) {
    const normalizedEmail = email.toLowerCase().trim();

    const allowed = await isEmailWhitelisted(normalizedEmail);
    if (!allowed) {
      throw new Error(
        "ACCESS_DENIED: This email is not on the approved client list. " +
        "Please contact info@aimbletech.com to request access."
      );
    }

    return signInWithEmailAndPassword(auth, normalizedEmail, password);
  }

  async function registerWithEmail(email, password, name) {
    const normalizedEmail = email.toLowerCase().trim();

    const allowed = await isEmailWhitelisted(normalizedEmail);
    if (!allowed) {
      throw new Error(
        "ACCESS_DENIED: Registration is by invitation only. " +
        "Please contact info@aimbletech.com to request access."
      );
    }

    const result = await createUserWithEmailAndPassword(auth, normalizedEmail, password);
    await updateProfile(result.user, { displayName: name });
    await createUserDoc(result.user, { displayName: name });
    return result;
  }

  function logout() {
    setUserProfile(null);
    return signOut(auth);
  }

  async function refreshProfile() {
    if (!currentUser) return;
    const snap = await getDoc(doc(db, "users", currentUser.uid));
    if (snap.exists()) setUserProfile(snap.data());
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        const snap = await getDoc(doc(db, "users", user.uid));
        if (snap.exists()) setUserProfile(snap.data());
        else setUserProfile(null);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const value = {
    currentUser,
    userProfile,
    signInWithGoogle,
    signInWithFacebook,
    signInWithEmail,
    registerWithEmail,
    logout,
    refreshProfile,
    isAdmin: userProfile?.role === "admin",
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}